import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, Circle, GaugeCircle, History as HistoryIcon,
  RefreshCw, Settings2, ShieldAlert, Zap, XCircle,
} from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import i18next from 'i18next';

const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);

// Adj-2 readiness band → visual mapping (founder-locked colours).
const BAND_META = {
  insufficient: { en: 'Insufficient', es: 'Insuficiente',           color: 'text-rose-700',    bg: 'bg-rose-50',    ring: 'stroke-rose-500',    border: 'border-rose-200' },
  needs_more:   { en: 'Needs more evidence', es: 'Necesita más evidencia',    color: 'text-amber-700',   bg: 'bg-amber-50',   ring: 'stroke-amber-500',   border: 'border-amber-200' },
  ready:        { en: 'Ready for transition', es: 'Lista para transición',   color: 'text-emerald-700', bg: 'bg-emerald-50', ring: 'stroke-emerald-500', border: 'border-emerald-200' },
};

const STAGE_TARGETS = [
  { value: 'findings',        en: 'Findings (Stage 4)', es: 'Hallazgos (Etapa 4)',        next: 4 },
  { value: 'recommendations', en: 'Recommendations (Stage 5)', es: 'Recomendaciones (Etapa 5)', next: 5 },
  { value: 'closure',         en: 'Closure (Stage 6)', es: 'Cierre (Etapa 6)',         next: 6 },
];

const APPROVAL_KIND_META = {
  'co-sign': { en: 'Co-signed', es: 'Co-firmada',     color: 'bg-emerald-100 text-emerald-700' },
  force:     { en: 'Force-override', es: 'Anulación forzada',color: 'bg-amber-100 text-amber-800' },
  denied:    { en: 'Denied', es: 'Denegada',        color: 'bg-rose-100 text-rose-700' },
  reopen:    { en: 'Re-opened', es: 'Reabierta',     color: 'bg-violet-100 text-violet-700' },
};

const fmtDt = (s) => (s ? String(s).slice(0, 16).replace('T', ' ') : '—');

// ---------------------------------------------------------------------
// Score ring (SVG)
// ---------------------------------------------------------------------
function ScoreRing({ score = 0, band = 'insufficient', size = 132 }) {
  const meta = BAND_META[band] || BAND_META.insufficient;
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" data-testid="tf6-score-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="rotate-[-90deg]">
        <circle cx={size / 2} cy={size / 2} r={r} className="fill-none stroke-slate-200" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} className={`fill-none ${meta.ring}`} strokeWidth="10"
                strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-semibold tabular-nums ${meta.color}`}>{Math.round(score)}</span>
        <span className="text-xs uppercase tracking-wide text-slate-500">{L('of 100','de 100')}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Per-rule chip
// ---------------------------------------------------------------------
function RuleRow({ rule, locale = 'en' }) {
  const label = rule.label?.[locale] || rule.label?.en || rule.key;
  let icon, tone;
  if (rule.status === 'pass')   { icon = <CheckCircle2 className="h-4 w-4 text-emerald-600" />; tone = 'bg-emerald-50 border-emerald-200'; }
  else if (rule.status === 'fail' && rule.is_blocking) { icon = <XCircle className="h-4 w-4 text-rose-600" />; tone = 'bg-rose-50 border-rose-200'; }
  else if (rule.status === 'fail') { icon = <AlertTriangle className="h-4 w-4 text-amber-600" />; tone = 'bg-amber-50 border-amber-200'; }
  else                              { icon = <Circle className="h-4 w-4 text-slate-400" />;       tone = 'bg-slate-50 border-slate-200'; }
  const cv = Array.isArray(rule.current_value) ? rule.current_value.join(', ') : rule.current_value;
  const tv = Array.isArray(rule.threshold_value) ? rule.threshold_value.join(', ') : rule.threshold_value;
  return (
    <div className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${tone}`}
         data-testid={`tf6-rule-row-${rule.key}`}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {icon}
        <div className="min-w-0">
          <p className="font-medium text-slate-800 truncate">{label}</p>
          <p className="text-xs text-slate-500">
            {rule.status === 'skipped' ? 'Deactivated' : <>now {cv ?? 0} / required {tv ?? 0}</>}
            {rule.is_blocking && <span className="ml-1 text-rose-700 font-medium">· blocking</span>}
          </p>
        </div>
      </div>
      <Badge variant="outline" className="text-xs shrink-0">w {rule.weight}</Badge>
    </div>
  );
}

// ---------------------------------------------------------------------
// Request Transition Dialog
// ---------------------------------------------------------------------
function RequestTransitionDialog({ open, onOpenChange, caseId, toStage, onSuccess }) {
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) { setRationale(''); } }, [open]);

  const submit = async () => {
    if (rationale.trim().length < 10) { toast.error(L('Rationale must be 10+ characters','La justificación debe tener 10+ caracteres')); return; }
    setSubmitting(true);
    try {
      await adminApi.tf6RequestTransition(caseId, { to_stage: toStage, rationale: rationale.trim() });
      toast.success(L('Transition requested — awaiting HM Admin co-sign','Transición solicitada — esperando co-firma del HM Admin'));
      onOpenChange(false); onSuccess?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === 'sufficiency_not_met') {
        toast.error(`Blocked: ${(detail.blocking_failed || []).length} blocking rule(s) failing (score ${detail.score})`);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Request failed');
      }
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf6-request-transition-dialog">
        <DialogHeader>
          <DialogTitle>Request transition → Stage {toStage}</DialogTitle>
          <DialogDescription>
            Stage transitions require HM Admin co-sign. Provide context for the approver.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={rationale} onChange={(e) => setRationale(e.target.value)}
                  placeholder={L('Why is this case ready to advance? (10+ chars)','¿Por qué está listo este caso para avanzar? (10+ caracteres)')}
                  data-testid="tf6-request-rationale" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf6-request-submit">
            {submitting ? 'Sending…' : 'Send request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Approve / Deny Dialogs
// ---------------------------------------------------------------------
function ApproveDialog({ open, onOpenChange, caseId, onSuccess }) {
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setRationale(''); }, [open]);
  const submit = async () => {
    if (rationale.trim().length < 10) { toast.error(L('Co-sign rationale must be 10+ chars','La justificación de co-firma debe tener 10+ caracteres')); return; }
    setSubmitting(true);
    try {
      await adminApi.tf6ApproveTransition(caseId, { rationale: rationale.trim() });
      toast.success(L('Transition approved — case advanced','Transición aprobada — el caso avanzó'));
      onOpenChange(false); onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Approve failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf6-approve-dialog">
        <DialogHeader>
          <DialogTitle>{L('Approve transition','Aprobar transición')}</DialogTitle>
          <DialogDescription>Your co-sign is recorded with timestamp + rationale.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)}
                  placeholder={L('Co-sign rationale (10+ chars)','Justificación de co-firma (10+ caracteres)')} data-testid="tf6-approve-rationale" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} data-testid="tf6-approve-submit">
            {submitting ? 'Approving…' : 'Approve & advance'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DenyDialog({ open, onOpenChange, caseId, onSuccess }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setReason(''); }, [open]);
  const submit = async () => {
    if (reason.trim().length < 5) { toast.error(L('Denial reason 5+ chars required','El motivo de denegación requiere 5+ caracteres')); return; }
    setSubmitting(true);
    try {
      await adminApi.tf6DenyTransition(caseId, { denial_reason: reason.trim() });
      toast.success(L('Transition denied','Transición denegada'));
      onOpenChange(false); onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Deny failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf6-deny-dialog">
        <DialogHeader>
          <DialogTitle>{L('Deny transition','Denegar transición')}</DialogTitle>
          <DialogDescription>{L('Reason is shared with the requesting advisor.','El motivo se comparte con el consultor solicitante.')}</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder={L('Why deny? (5+ chars)','¿Por qué denegar? (5+ caracteres)')} data-testid="tf6-deny-reason" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Back</Button>
          <Button variant="destructive" onClick={submit} disabled={submitting} data-testid="tf6-deny-submit">
            {submitting ? 'Denying…' : 'Deny'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Force Override Dialog (HM Admin only)
// ---------------------------------------------------------------------
function ForceOverrideDialog({ open, onOpenChange, caseId, toStage, onSuccess }) {
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (open) setRationale(''); }, [open]);
  const submit = async () => {
    if (rationale.trim().length < 20) { toast.error(L('Force-override rationale must be 20+ chars','La justificación de anulación forzada debe tener 20+ caracteres')); return; }
    setSubmitting(true);
    try {
      const res = await adminApi.tf6ForceTransition(caseId, { to_stage: toStage, rationale: rationale.trim() });
      toast.success(`Forced to Stage ${res.current_stage} · original score ${res.original_score}`);
      onOpenChange(false); onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Force failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="tf6-force-override-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-1.5 text-amber-800">
            <Zap className="h-4 w-4" />Force-override → Stage {toStage}
          </DialogTitle>
          <DialogDescription>
            Bypasses the sufficiency gate. Rationale is permanently audited (20+ chars; original score is captured).
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={4} value={rationale} onChange={(e) => setRationale(e.target.value)}
                  placeholder={L('Why is force-override justified? (20+ chars — legal-reviewable)','¿Por qué se justifica la anulación forzada? (20+ caracteres — revisable legalmente)')}
                  data-testid="tf6-force-rationale" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{L('Cancel','Cancelar')}</Button>
          <Button onClick={submit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="tf6-force-submit">
            {submitting ? 'Forcing…' : 'Force override'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------
// Stage history accordion
// ---------------------------------------------------------------------
function StageHistoryAccordion({ items, locale = 'en' }) {
  if (!items || items.length === 0) {
    return <p className="text-xs italic text-slate-500" data-testid="tf6-stage-history-empty">{L('No transitions yet.','Aún no hay transiciones.')}</p>;
  }
  return (
    <div className="space-y-2" data-testid="tf6-stage-history">
      {items.map((row) => {
        const meta = APPROVAL_KIND_META[row.approval_kind] || { label: row.approval_kind, color: 'bg-slate-100 text-slate-700' };
        return (
          <div key={row.id} className="rounded-md border border-slate-200 bg-white p-3 text-xs"
               data-testid={`tf6-stage-history-row-${row.id}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="inline-flex items-center gap-2">
                <Badge className={meta.color}>{L(meta.en, meta.es)}</Badge>
                <span className="font-medium text-slate-800">
                  Stage {row.from_stage ?? '—'} → {row.to_stage}
                </span>
              </div>
              <span className="text-xs text-slate-500">{fmtDt(row.approved_at)}</span>
            </div>
            {row.sufficiency_snapshot?.score !== undefined && (
              <p className="mt-1 text-xs text-slate-500">
                Sufficiency at transition: <span className="font-medium">{row.sufficiency_snapshot.score}</span>
                {row.sufficiency_snapshot.band && (
                  <span className="ml-1">· {L(BAND_META[row.sufficiency_snapshot.band]?.en, BAND_META[row.sufficiency_snapshot.band]?.es) || row.sufficiency_snapshot.band}</span>
                )}
              </p>
            )}
            {row.original_score !== undefined && row.original_score !== null && (
              <p className="mt-1 text-xs text-amber-700">
                Force original score: <span className="font-medium">{row.original_score}</span>
              </p>
            )}
            {row.rationale_request && (
              <p className="mt-1 text-xs text-slate-600 italic">“{row.rationale_request}”</p>
            )}
            {row.denial_reason && (
              <p className="mt-1 text-xs text-rose-700">Denial: {row.denial_reason}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------
// Sufficiency Tab — main export
// ---------------------------------------------------------------------
export default function SufficiencyTab({ caseId, companyId, isHmAdmin, currentUserId, locale = 'en' }) {
  const [stageTarget, setStageTarget] = useState('findings');
  const [loading, setLoading] = useState(true);
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentStage, setCurrentStage] = useState(null);
  const [pending, setPending] = useState(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [denyOpen, setDenyOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  const load = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        forceRefresh
          ? adminApi.tf6RefreshSufficiency(caseId, stageTarget)
          : adminApi.tf6GetSufficiency(caseId, stageTarget),
        adminApi.tf6StageHistory(caseId),
      ]);
      setSnap(s);
      setHistory(h.items || []);
      setCurrentStage(h.current_stage ?? null);
      setPending(h.pending_transition || null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d === 'sufficiency_disabled') toast.error(L('Sufficiency engine is disabled (kill-switch on)','El motor de suficiencia está deshabilitado (interruptor activado)'));
      else toast.error(L('Could not load sufficiency snapshot','No se pudo cargar el snapshot de suficiencia'));
    } finally { setLoading(false); }
  }, [caseId, stageTarget]);

  useEffect(() => { load(); }, [load]);

  const band = snap?.band || 'insufficient';
  const meta = BAND_META[band];
  const blockingFailed = snap?.blocking_failed || [];
  const blockedByRules = blockingFailed.length > 0;
  const targetCfg = STAGE_TARGETS.find((s) => s.value === stageTarget);
  const targetStage = targetCfg?.next;
  const isSequential = (currentStage ?? 0) + 1 === targetStage;
  const requestDisabled = blockedByRules || !isSequential || !!pending || (currentStage ?? 0) >= 6;
  const canApprove = isHmAdmin && pending && pending.requested_by_user_id !== currentUserId;
  const isClosed = (currentStage ?? 0) >= 6;

  const orderedRules = useMemo(() => {
    const r = (snap?.rules || []).slice();
    const rank = (rl) => {
      if (rl.status === 'fail' && rl.is_blocking) return 0;
      if (rl.status === 'fail') return 1;
      if (rl.status === 'pass') return 2;
      return 3;
    };
    r.sort((a, b) => rank(a) - rank(b));
    return r;
  }, [snap]);

  return (
    <div className="space-y-6" data-testid="tf6-sufficiency-tab">
      {/* Top control row */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Select value={stageTarget} onValueChange={setStageTarget}>
            <SelectTrigger className="w-[260px]" data-testid="tf6-stage-target-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_TARGETS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{L(s.en, s.es)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => load(true)} data-testid="tf6-refresh-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
          </Button>
        </div>
        {isHmAdmin && (
          <Button asChild variant="ghost" size="sm" data-testid="tf6-rule-editor-link">
            <Link to={`/admin/talentfix/sufficiency-rules?company_id=${companyId || ''}&stage_target=${stageTarget}`}>
              <Settings2 className="h-3.5 w-3.5 mr-1" />Configure rules
            </Link>
          </Button>
        )}
      </div>

      {loading && !snap ? (
        <p className="text-sm italic text-slate-500">Computing sufficiency…</p>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr_280px]">
          {/* Left — score ring + band + current stage */}
          <Card className={`${meta?.bg} ${meta?.border}`} data-testid={`tf6-score-pill-${caseId}`}>
            <CardContent className="pt-6 flex flex-col items-center text-center gap-3">
              <ScoreRing score={snap?.score ?? 0} band={band} />
              <div>
                <Badge variant="outline" className={`${meta?.color} border-current`} data-testid="tf6-band-label">
                  {meta ? L(meta.en, meta.es) : null}
                </Badge>
                <p className="mt-2 text-xs text-slate-600">
                  Current stage: <span className="font-medium" data-testid="tf6-current-stage">{currentStage ?? '—'}</span>
                </p>
                {pending && (
                  <p className="mt-1 text-xs text-amber-700">
                    Pending → Stage {pending.to_stage}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Centre — rule breakdown + transition actions */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base inline-flex items-center gap-1.5">
                  <GaugeCircle className="h-4 w-4 text-slate-500" />Rule breakdown
                </CardTitle>
                <CardDescription>
                  {orderedRules.length} rule{orderedRules.length === 1 ? '' : 's'} evaluated · {blockingFailed.length} blocking failure{blockingFailed.length === 1 ? '' : 's'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {orderedRules.length === 0
                  ? <p className="text-xs italic text-slate-500">{L('No rules configured for this stage target.','No hay reglas configuradas para esta etapa objetivo.')}</p>
                  : orderedRules.map((r) => <RuleRow key={r.key} rule={r} locale={locale} />)
                }
              </CardContent>
            </Card>

            {/* Action row */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{L('Stage transition','Transición de etapa')}</CardTitle>
                <CardDescription>
                  {isClosed
                    ? 'Case is closed (Stage 6). Re-open requires HM Admin authorisation.'
                    : pending
                      ? `Pending request to Stage ${pending.to_stage} — awaiting co-sign.`
                      : blockedByRules
                        ? 'Resolve blocking rules before requesting transition.'
                        : `Ready to request transition to Stage ${targetStage}.`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isClosed && isHmAdmin ? (
                  <ReopenButton caseId={caseId} onSuccess={() => load(true)} />
                ) : (
                  <TooltipProvider>
                    <div className="flex flex-wrap gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button onClick={() => setRequestOpen(true)} disabled={requestDisabled}
                                    data-testid="tf6-request-transition-btn">
                              Request transition → Stage {targetStage}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {requestDisabled && (
                          <TooltipContent>
                            {pending ? 'Already pending' :
                             !isSequential ? `Sequential only — current stage ${currentStage}` :
                             'Blocking rule(s) failing'}
                          </TooltipContent>
                        )}
                      </Tooltip>

                      {pending && isHmAdmin && (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button variant="outline" disabled={!canApprove} onClick={() => setApproveOpen(true)}
                                        data-testid={`tf6-approve-transition-btn-${caseId}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" />Approve
                                </Button>
                              </span>
                            </TooltipTrigger>
                            {!canApprove && (
                              <TooltipContent>{L('You requested this transition — another HM Admin must co-sign','Tú solicitaste esta transición — otro HM Admin debe co-firmar')}</TooltipContent>
                            )}
                          </Tooltip>
                          <Button variant="outline" onClick={() => setDenyOpen(true)}
                                  data-testid={`tf6-deny-transition-btn-${caseId}`}>
                            <XCircle className="h-3.5 w-3.5 mr-1 text-rose-600" />Deny
                          </Button>
                        </>
                      )}

                      {isHmAdmin && !isClosed && (
                        <Button variant="outline" onClick={() => setForceOpen(true)}
                                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                                data-testid="tf6-force-override-btn">
                          <Zap className="h-3.5 w-3.5 mr-1" />Force-override
                        </Button>
                      )}
                    </div>
                  </TooltipProvider>
                )}
                {blockedByRules && (
                  <p className="mt-3 text-xs text-rose-700 inline-flex items-center gap-1">
                    <ShieldAlert className="h-3 w-3" />Blocked by: {blockingFailed.join(', ')}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right — stage history */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base inline-flex items-center gap-1.5">
                <HistoryIcon className="h-4 w-4 text-slate-500" />Stage history
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StageHistoryAccordion items={history} locale={locale} />
            </CardContent>
          </Card>
        </div>
      )}

      <RequestTransitionDialog open={requestOpen} onOpenChange={setRequestOpen}
        caseId={caseId} toStage={targetStage} onSuccess={() => load(true)} />
      <ApproveDialog open={approveOpen} onOpenChange={setApproveOpen}
        caseId={caseId} onSuccess={() => load(true)} />
      <DenyDialog open={denyOpen} onOpenChange={setDenyOpen}
        caseId={caseId} onSuccess={() => load(true)} />
      <ForceOverrideDialog open={forceOpen} onOpenChange={setForceOpen}
        caseId={caseId} toStage={targetStage} onSuccess={() => load(true)} />
    </div>
  );
}

// Re-open helper (HM-Admin-only Stage-6 → 5 escape hatch)
function ReopenButton({ caseId, onSuccess }) {
  const [open, setOpen] = useState(false);
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (rationale.trim().length < 10) { toast.error(L('Rationale 10+ chars','Justificación de 10+ caracteres')); return; }
    setSubmitting(true);
    try {
      await adminApi.tf6ReopenCase(caseId, { rationale: rationale.trim() });
      toast.success(L('Case re-opened to Stage 5','Caso reabierto a la Etapa 5'));
      setOpen(false); onSuccess?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Re-open failed');
    } finally { setSubmitting(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="tf6-reopen-btn">
        <HistoryIcon className="h-3.5 w-3.5 mr-1" />Re-open case
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tf6-reopen-dialog">
          <DialogHeader>
            <DialogTitle>{L('Re-open closed case','Reabrir caso cerrado')}</DialogTitle>
            <DialogDescription>{L('Returns the case to Stage 5 with an immutable audit row.','Devuelve el caso a la Etapa 5 con un registro de auditoría inmutable.')}</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={rationale} onChange={(e) => setRationale(e.target.value)}
                    placeholder={L('Why re-open this case? (10+ chars)','¿Por qué reabrir este caso? (10+ caracteres)')} data-testid="tf6-reopen-rationale" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{L('Cancel','Cancelar')}</Button>
            <Button onClick={submit} disabled={submitting} data-testid="tf6-reopen-submit">
              {submitting ? 'Re-opening…' : 'Re-open'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
